import { type Dispatch, type SetStateAction, useRef } from 'react';
import { useUnmountEffect } from '@/lib/react/hooks';
import { scheduleMicrotask } from '@/lib/utils/scheduleMicrotask';
import { setWindowTimeout } from '@/lib/utils/window-timers';
import { CLOSE_LINGER_MS, FAST_PREFETCH_DELAY_MS, SLOW_NETWORK_FALLBACK_MS } from './constants';
import type { HoverCardCoordinator } from './hover-card-coordinator';
import type { HoverPhase } from './types';

type WritableReference<T> = { current: T };

interface PreviewHoverTimersConfig {
  activeHoverId: string | null;
  clearCloseTimer: () => void;
  clearPrimingTimers: () => void;
  closeTimerRef: WritableReference<number | null>;
  coordinator: HoverCardCoordinator;
  fastGatePassed: boolean;
  fastGateTimerRef: WritableReference<number | null>;
  hasLoggedHoverRef: WritableReference<boolean>;
  isCurrentHover: boolean;
  isDataReady: boolean;
  phase: HoverPhase;
  pointerInside: boolean;
  prefetchedHasData: boolean;
  previewId: string;
  resetHoverState: () => void;
  setFastGatePassed: Dispatch<SetStateAction<boolean>>;
  setPhase: Dispatch<SetStateAction<HoverPhase>>;
  setShowFallbackLoading: Dispatch<SetStateAction<boolean>>;
  slowFallbackTimerRef: WritableReference<number | null>;
}

export function usePreviewHoverTimers({
  phase,
  pointerInside,
  prefetchedHasData,
  isDataReady,
  fastGatePassed,
  activeHoverId,
  isCurrentHover,
  previewId,
  coordinator,
  clearPrimingTimers,
  clearCloseTimer,
  resetHoverState,
  setFastGatePassed,
  setShowFallbackLoading,
  setPhase,
  fastGateTimerRef,
  slowFallbackTimerRef,
  closeTimerRef,
  hasLoggedHoverRef,
}: PreviewHoverTimersConfig): void {
  const latestPointerInsideReference = useRef(pointerInside);
  latestPointerInsideReference.current = pointerInside;

  const latestIsDataReadyReference = useRef(isDataReady);
  latestIsDataReadyReference.current = isDataReady;

  const latestFastGatePassedReference = useRef(fastGatePassed);
  latestFastGatePassedReference.current = fastGatePassed;

  const latestPhaseReference = useRef(phase);
  latestPhaseReference.current = phase;

  const latestActiveHoverIdReference = useRef(activeHoverId);
  latestActiveHoverIdReference.current = activeHoverId;

  const latestIsCurrentHoverReference = useRef(isCurrentHover);
  latestIsCurrentHoverReference.current = isCurrentHover;

  const schedulePrimingReference = useRef(false);
  const primingSnapshotReference = useRef<{
    phase: HoverPhase;
    prefetchedHasData: boolean;
    pointerInside: boolean;
  } | null>(null);

  const nextPrimingSnapshot = { phase, prefetchedHasData, pointerInside };
  const previousPrimingSnapshot = primingSnapshotReference.current;
  if (
    !previousPrimingSnapshot ||
    previousPrimingSnapshot.phase !== nextPrimingSnapshot.phase ||
    previousPrimingSnapshot.prefetchedHasData !== nextPrimingSnapshot.prefetchedHasData ||
    previousPrimingSnapshot.pointerInside !== nextPrimingSnapshot.pointerInside
  ) {
    primingSnapshotReference.current = nextPrimingSnapshot;
    if (!schedulePrimingReference.current) {
      schedulePrimingReference.current = true;
      scheduleMicrotask(() => {
        schedulePrimingReference.current = false;
        const snapshot = primingSnapshotReference.current;
        if (!snapshot) {
          return;
        }
        if (snapshot.phase !== 'priming') {
          if (snapshot.phase === 'idle') {
            setFastGatePassed(snapshot.prefetchedHasData);
            setShowFallbackLoading(false);
          }
          clearPrimingTimers();
          return;
        }

        clearPrimingTimers();

        if (snapshot.prefetchedHasData) {
          setFastGatePassed(true);
          setShowFallbackLoading(false);
          return;
        }

        if (fastGateTimerRef.current === null) {
          fastGateTimerRef.current = setWindowTimeout(() => {
            fastGateTimerRef.current = null;
            setFastGatePassed(true);
          }, FAST_PREFETCH_DELAY_MS);
        }

        if (slowFallbackTimerRef.current === null) {
          slowFallbackTimerRef.current = setWindowTimeout(() => {
            slowFallbackTimerRef.current = null;
            setShowFallbackLoading(true);
            if (latestPointerInsideReference.current) {
              setPhase(() => 'open');
            }
          }, SLOW_NETWORK_FALLBACK_MS);
        }
      });
    }
  }

  const scheduleCloseReference = useRef(false);
  const closePhaseReference = useRef<HoverPhase | null>(null);
  if (closePhaseReference.current !== phase) {
    closePhaseReference.current = phase;
    if (!scheduleCloseReference.current) {
      scheduleCloseReference.current = true;
      scheduleMicrotask(() => {
        scheduleCloseReference.current = false;
        if (latestPhaseReference.current !== 'closing') {
          clearCloseTimer();
          return;
        }

        if (closeTimerRef.current) {
          return;
        }

        closeTimerRef.current = setWindowTimeout(() => {
          closeTimerRef.current = null;
          resetHoverState();
        }, CLOSE_LINGER_MS);
      });
    }
  }

  const shouldOpenStateReference = useRef(false);
  if (phase === 'priming' && pointerInside && fastGatePassed && isDataReady) {
    if (!shouldOpenStateReference.current) {
      shouldOpenStateReference.current = true;
      scheduleMicrotask(() => {
        if (
          latestPhaseReference.current === 'priming' &&
          latestPointerInsideReference.current &&
          latestFastGatePassedReference.current &&
          latestIsDataReadyReference.current
        ) {
          setShowFallbackLoading(false);
          setPhase(() => 'open');
        }
      });
    }
  } else {
    shouldOpenStateReference.current = false;
  }

  const forceCloseTrackerReference = useRef(false);
  const shouldForceClose = !isCurrentHover && pointerInside && Boolean(activeHoverId);
  if (shouldForceClose && !forceCloseTrackerReference.current) {
    forceCloseTrackerReference.current = true;
    scheduleMicrotask(() => {
      if (
        !latestIsCurrentHoverReference.current &&
        latestPointerInsideReference.current &&
        latestActiveHoverIdReference.current
      ) {
        coordinator.close(previewId);
        hasLoggedHoverRef.current = false;
        resetHoverState();
      }
    });
  } else if (!shouldForceClose) {
    forceCloseTrackerReference.current = false;
  }
  useUnmountEffect(() => {
    clearPrimingTimers();
    clearCloseTimer();
  });
}

export type { PreviewHoverTimersConfig };
